module Client.Page.Admin.PhysicalSensorList

open System
open System.Collections.Generic
open Client.Components
open Client.Components.Select
open Client.Domain
open Client.Domain.MeasuredValueString
open Client.DomainTypes.Select
open Client.Infrastructure
open Client.InfrastructureTypes
open Client
open Client.Infrastructure.Api
open Client.DomainTypes.Msg
open Fulma.Extensions.Wikiki

open Elmish
open Fable.FontAwesome
open Fable.React.Props
open Fulma
open Shared.Dto.Dto
open Fable.React
open Shared.Dto.User
open Shared.DtoTypes
open Shared.DtoTypes.DeviceLocation
open Shared.DtoTypes.PhysicalSensor
open Shared.Infrastructure
open Thoth.Elmish

type DataModel = {
    AllSensors: PhysicalSensorWithLastData list
    Users: IDictionary<int, UserDto>
    DeviceLocations: DeviceLocation list
    DisplayedSensors: PhysicalSensorWithLastData list
    TTNRefreshButton: PhysicalSensorRefreshButton.Model
    TTIRefreshButton: PhysicalSensorRefreshButton.Model
    Session: UserSession
    Modal: Forms.PhysicalSensor.Model option
    DisplayFilters: bool
    NameFilter: string option
    DeviceEuiFilter: string option
    NoteFilter: string option
    UserFilter: UserFilter.State
    LocationFilter: DeviceLocation option
    ConditionFilter: DeviceCondition option
}

type LoadingModel = {
    Session: UserSession
    Users: UserDto list option
    Locations: DeviceLocation list option
    AllSensors: PhysicalSensorWithLastData list option
}

type Model = Loadable<DataModel, LoadingModel>

let init (session: UserSession) =
    let requestData = {
        SessionKey = session.SessionKey
        Data = ()
    }

    let cmds =
        Cmd.batch [
            Cmd.OfAsync.perform api.getAllPhysicalSensors () (PhysicalSensorListMsg.ListReceived >> PhysicalSensorList)
            Cmd.OfAsync.perform api.getAllUsers requestData (PhysicalSensorListMsg.UsersReceived >> PhysicalSensorList)
            Cmd.OfAsync.perform
                api.getAllDeviceLocations
                requestData
                (PhysicalSensorListMsg.DeviceLocationsReceived >> PhysicalSensorList)
        ]

    let loadingModel = {
        Session = session
        AllSensors = None
        Users = None
        Locations = None
    }

    Loadable.Loading loadingModel, cmds

let private createDataModel (loadingModel: LoadingModel) =
    match loadingModel.Users, loadingModel.AllSensors, loadingModel.Locations with
    | Some users, Some sensors, Some locations ->
        Loadable.Data {
            AllSensors = sensors
            Users = users |> List.map (fun user -> user.Id, user) |> dict
            DeviceLocations = locations
            DisplayedSensors = sensors
            Session = loadingModel.Session
            Modal = None
            TTNRefreshButton = PhysicalSensorRefreshButton.Waiting
            TTIRefreshButton = PhysicalSensorRefreshButton.Waiting
            DisplayFilters = false
            NameFilter = None
            DeviceEuiFilter = None
            NoteFilter = None
            UserFilter = UserFilter.NoFilter
            LocationFilter = None
            ConditionFilter = None
        }
    | _, _, _ -> Loadable.Loading loadingModel

let updateRequestRunning (model: DataModel) (requestRunning: bool) =
    let modalModel = Option.get model.Modal

    { model with Modal = Some { modalModel with RequestRunning = requestRunning } }

let private filter (data: DataModel) (sensor: PhysicalSensorWithLastData) : bool =
    let nameMatch =
        match data.NameFilter with
        | Some filter -> String.containsIgnoreCase (getSensorName sensor.Sensor) filter
        | None -> true

    let deviceEuiMatch =
        match data.DeviceEuiFilter with
        | Some filter -> String.containsIgnoreCase (getSensorDeviceEui sensor.Sensor) filter
        | None -> true

    let descriptionMatch =
        match data.NoteFilter, getDescription sensor.Sensor with
        | Some filter, Some description -> String.containsIgnoreCase filter description
        | Some _, None -> false
        | None, _ -> true

    let userFilterMatch =
        UserFilter.applyFilter (getBaseData sensor.Sensor).UserId data.UserFilter

    let locationFilterMatch =
        match data.LocationFilter with
        | Some location -> (getBaseData sensor.Sensor).LocationId = Some location.Id
        | None -> true

    let conditionFilterMatch =
        match data.ConditionFilter with
        | Some condition -> (getBaseData sensor.Sensor).Condition = condition
        | None -> true

    nameMatch
    && deviceEuiMatch
    && descriptionMatch
    && userFilterMatch
    && locationFilterMatch
    && conditionFilterMatch

let private updateDisplayedSensors (data: DataModel) =
    let displayedSensors = List.filter (filter data) data.AllSensors

    { data with DisplayedSensors = displayedSensors }

let private updateRefreshButtonState
    (f: PhysicalSensorRefreshButton.Model -> DataModel -> DataModel)
    (result: PhysicalSensorRefresh.Result)
    (model: DataModel)
    : Model * Cmd<'msg> =
    let newModel, buttonState =
        match result with
        | PhysicalSensorRefresh.RefreshFailed message ->
            model, PhysicalSensorRefreshButton.Finished(PhysicalSensorRefreshButton.FinishedState.Error message)
        | PhysicalSensorRefresh.Refreshed result ->
            let newModel = { model with AllSensors = result.Sensors } |> updateDisplayedSensors

            newModel, PhysicalSensorRefreshButton.modelFromResult result

    newModel |> f buttonState |> Loadable.Data, Cmd.none

let update (msg: PhysicalSensorListMsg) (model: Model) =
    match msg, model with
    | PhysicalSensorListMsg.RefreshTTNSensors, Loadable.Data data ->
        let request = {
            SessionKey = data.Session.SessionKey
            Data = ()
        }

        let cmd =
            Cmd.OfAsync.perform
                api.refreshTtnEndDevices
                request
                (PhysicalSensorListMsg.TtnListRefreshed >> PhysicalSensorList)

        Loadable.Data { data with TTNRefreshButton = PhysicalSensorRefreshButton.RefreshRunning }, cmd
    | PhysicalSensorListMsg.RefreshTTISensors, Loadable.Data data ->
        let request = {
            SessionKey = data.Session.SessionKey
            Data = ()
        }

        let cmd =
            Cmd.OfAsync.perform
                api.refreshTtiEndDevices
                request
                (PhysicalSensorListMsg.TtiListRefreshed >> PhysicalSensorList)

        Loadable.Data { data with TTIRefreshButton = PhysicalSensorRefreshButton.RefreshRunning }, cmd
    | PhysicalSensorListMsg.TtiListRefreshed result, Loadable.Data dataModel ->
        let updater =
            fun buttonState dataModel -> { dataModel with TTIRefreshButton = buttonState }

        updateRefreshButtonState updater result dataModel

    | PhysicalSensorListMsg.TtnListRefreshed result, Loadable.Data dataModel ->
        let updater =
            fun buttonState dataModel -> { dataModel with TTNRefreshButton = buttonState }

        updateRefreshButtonState updater result dataModel

    | PhysicalSensorListMsg.ListReceived sensors, Loadable.Loading loadingModel ->
        createDataModel { loadingModel with AllSensors = Some sensors }, Cmd.none
    | PhysicalSensorListMsg.UsersReceived response, Loadable.Loading loadingModel ->
        match response with
        | Result.Ok users -> createDataModel { loadingModel with Users = Some users }, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | PhysicalSensorListMsg.DeviceLocationsReceived response, Loadable.Loading loadingModel ->
        match response with
        | Result.Ok locations -> createDataModel { loadingModel with Locations = Some locations }, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | PhysicalSensorListMsg.CloseModal, Loadable.Data data -> Loadable.Data { data with Modal = None }, Cmd.none
    | PhysicalSensorListMsg.OpenNewSensorModal, Loadable.Data data ->
        Loadable.Data {
            data with
                Modal = Some(Forms.PhysicalSensor.init data.DeviceLocations (List.ofDictValues data.Users) None)
        },
        Cmd.none
    | PhysicalSensorListMsg.EditSensorModal sensor, Loadable.Data data ->
        Loadable.Data {
            data with
                Modal =
                    Some(Forms.PhysicalSensor.init data.DeviceLocations (List.ofDictValues data.Users) (Some sensor))
        },
        Cmd.none
    | PhysicalSensorListMsg.Form formMsg, Loadable.Data data ->
        match (formMsg, data.Modal) with
        | PhysicalSensorFormMsg.SelectedSensorModelChanged selectedType, Some modal ->
            let modalModel = { modal with SelectedModel = selectedType }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.NameChanged name, Some modal ->
            let modalModel = { modal with Name = name }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.AppEuiChanged appEui, Some modal ->
            let modalModel = { modal with AppEui = appEui }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.DeviceEuiChanged deviceEui, Some modal ->
            let modalModel = { modal with DeviceEui = deviceEui }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.AppKeyChanged appKey, Some modal ->
            let modalModel = { modal with AppKey = appKey }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.DescriptionChanged description, Some modal ->
            let modalModel = { modal with TtsDescription = description }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.NoteChanged note, Some modal ->
            let modalModel = { modal with Note = note }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.MaxHumidityChanged maxHumidity, Some modal ->
            let modalModel = { modal with MaxHumidity = maxHumidity }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.SelectedOwnerChanged owner, Some modal ->
            let modalModel = { modal with SelectedUser = owner }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.ConditionChanged condition, Some modal ->
            let modalModel = { modal with DeviceCondition = condition }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.LocationChanged location, Some modal ->
            let modalModel = { modal with SelectedLocation = location }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.PressureProbeChanged pressureProbe, Some modal ->
            let modalModel = { modal with SelectedPressureProbe = pressureProbe }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | PhysicalSensorFormMsg.LiquidProbeDepthChanged depth, Some modal ->
            let modalModel = { modal with LiquidProbeDepth = depth }

            Loadable.Data { data with Modal = Some modalModel }, Cmd.none
        | _ -> Loadable.Data data, Cmd.none


    | PhysicalSensorListMsg.CreateSensor sensor, Loadable.Data data ->
        let request = {
            SessionKey = data.Session.SessionKey
            Data = sensor
        }

        Loadable.Data(updateRequestRunning data true),

        Cmd.map
            PhysicalSensorList
            (Cmd.OfAsync.perform api.createPhysicalSensor request PhysicalSensorListMsg.SensorCreated)
    | PhysicalSensorListMsg.SensorCreated response, Loadable.Data data ->
        match response with
        | Ok(Some createdSensor) ->
            let toastCmd =
                Toast.create "Der Sensor wurde erfolgreich gespeichert" |> Toast.success

            let createdSensorWithDate = {
                Sensor = Configured createdSensor
                LastData = None
            }

            let newList =
                createdSensorWithDate :: data.AllSensors
                |> List.sortBy (fun sensor -> (getBaseData sensor.Sensor).Name)

            let newData =
                {
                    data with
                        AllSensors = newList
                        Modal = None
                }
                |> updateDisplayedSensors


            Loadable.Data newData, toastCmd
        | _ ->
            let toastCmd =
                Toast.create "Ein Fehler ist aufgetreten beim Speichern des Sensors"
                |> Toast.error

            Loadable.Data(updateRequestRunning data false), toastCmd
    | PhysicalSensorListMsg.UpdateSensor sensor, Loadable.Data data ->
        let request = {
            SessionKey = data.Session.SessionKey
            Data = sensor
        }

        Loadable.Data(updateRequestRunning data true),

        Cmd.map
            PhysicalSensorList
            (Cmd.OfAsync.perform api.updatePhysicalSensor request PhysicalSensorListMsg.SensorUpdated)
    | PhysicalSensorListMsg.SensorUpdated response, Loadable.Data data ->
        match response with
        | Ok(Some updatedSensor) ->
            let toastCmd =
                Toast.create "Der Sensor wurde erfolgreich aktualisiert" |> Toast.success

            let newList =
                List.filteredMap
                    (fun (sensor: PhysicalSensorWithLastData) ->
                        (getBaseData sensor.Sensor).DeviceEui = updatedSensor.BaseData.DeviceEui
                    )
                    (fun (sensor: PhysicalSensorWithLastData) -> { sensor with Sensor = Configured updatedSensor })
                    data.AllSensors

            let newData =
                {
                    data with
                        AllSensors = newList
                        Modal = None
                }
                |> updateDisplayedSensors


            Loadable.Data newData, toastCmd
        | _ ->
            let toastCmd =
                Toast.create "Ein Fehler ist aufgetreten beim Speichern des Sensors"
                |> Toast.error

            Loadable.Data(updateRequestRunning data false), toastCmd
    | PhysicalSensorListMsg.DeleteSensor sensor, Loadable.Data data ->
        let request = {
            SessionKey = data.Session.SessionKey
            Data = sensor
        }

        Loadable.Data { data with Modal = None },
        Cmd.map
            PhysicalSensorList
            (Cmd.OfAsync.perform api.removePhysicalSensor request PhysicalSensorListMsg.SensorDeleted)
    | PhysicalSensorListMsg.SensorDeleted response, Loadable.Data data ->
        match response with
        | Ok _ ->
            let toastCmd = Toast.create "Der Sensor wurde erfolgreich gelöscht" |> Toast.success

            init data.Session |> Cmds.batch toastCmd
        | _ ->
            let toastCmd =
                Toast.create "Ein Fehler ist aufgetreten beim Lößchen des Sensors"
                |> Toast.error

            Loadable.Data(updateRequestRunning data false), toastCmd
    | PhysicalSensorListMsg.ToggleFilterDisplay, Loadable.Data data ->
        Loadable.Data { data with DisplayFilters = not data.DisplayFilters }, Cmd.none
    | PhysicalSensorListMsg.NameFilterChanged name, Loadable.Data data ->
        let newData =
            { data with NameFilter = name } |> updateDisplayedSensors |> Loadable.Data

        newData, Cmd.none
    | PhysicalSensorListMsg.DeviceEuiFilterChanged eui, Loadable.Data data ->
        let newData =
            { data with DeviceEuiFilter = eui } |> updateDisplayedSensors |> Loadable.Data

        newData, Cmd.none
    | PhysicalSensorListMsg.NoteFilterChanged note, Loadable.Data data ->
        let newData =
            { data with NoteFilter = note } |> updateDisplayedSensors |> Loadable.Data

        newData, Cmd.none
    | PhysicalSensorListMsg.UserFilterChanged state, Loadable.Data data ->
        let newData =
            { data with UserFilter = state } |> updateDisplayedSensors |> Loadable.Data

        newData, Cmd.none
    | PhysicalSensorListMsg.LocationFilterChanged location, Loadable.Data data ->
        let newData =
            { data with LocationFilter = location }
            |> updateDisplayedSensors
            |> Loadable.Data

        newData, Cmd.none
    | PhysicalSensorListMsg.ConditionFilterChanged condition, Loadable.Data data ->
        let newData =
            { data with ConditionFilter = condition }
            |> updateDisplayedSensors
            |> Loadable.Data

        newData, Cmd.none
    | _, _ -> model, Cmd.none

let private expectedIntervalMinutes (model: SensorModel) =
    match model with
    | WSC1L -> 2
    | LHT65
    | LSN50v2_S31
    | S31_LS
    | S31_LB
    | LSN50v2_Wind
    | SN50v3_LS_Wind
    | SN50v3_LB_Wind
    | LSN50v2_Rain_01mm
    | LSN50v2_Rain_02mm
    | SN50v3_LS_Rain_01mm
    | SN50v3_LS_Rain_02mm
    | SN50v3_LB_Rain_01mm
    | SN50v3_LB_Rain_02mm
    | DDS20_LB
    | DDS20_LS
    | LDDS75
    | DDS75_LB
    | DDS75_LS -> 10
    | LSE01
    | SE01_LS
    | SE01_LB
    | SE0X_LB
    | LLMS01
    | LSPH01
    | SPH01_LB
    | SPH01_LS
    | RS485_LB_ZFS_02
    | RS485_LS_ZFS_02
    | PS_LB _
    | PS_LS _
    | WQS_LB
    | WQS_LS -> 20

let private dangerColor = "danger-color"
let private warningColor = "warning-color"

let private overFlowColor = "overflow-color"

let private createTableCell (maybeColorClass: string option) (content: ReactElement list) =
    td
        [
            match maybeColorClass with
            | Some colorClass -> Class colorClass
            | None -> ()
        ]
        content

let private createStringTableCell (colorClass: string option) (content: string) =
    createTableCell colorClass [ str content ]

let private createLastIntervalCell (sensorModel: SensorModel) (timespan: TimeSpan) =
    let expectedMinutes = expectedIntervalMinutes sensorModel

    let roundedMinutes = TimeSpan.toRoundedMinutes timespan

    let colorClass =
        if roundedMinutes <> expectedMinutes then
            Some dangerColor
        else
            None

    let content = [
        Icon.icon [] [ Fa.i [ Fa.Solid.History ] [] ]
        span [] [
            str (TimeSpan.countToString roundedMinutes "Minute" "Minuten")
        ]
    ]

    createTableCell colorClass content

let private getAdjustedHumidityColor (adjustedHumidity: float) =
    match adjustedHumidity with
    | x when adjustedHumidity <= 100.0 -> None
    | x when adjustedHumidity <= 100.5 -> Some overFlowColor
    | x when adjustedHumidity <= 103.0 -> Some warningColor
    | _ -> Some dangerColor

let private createLastHumidityCell (humidity: AirHumidity) =
    match humidity with
    | Original original -> createStringTableCell None (percentageToString original)
    | Adjusted adjustedHumidity ->
        let color = getAdjustedHumidityColor adjustedHumidity.UnboundedAdjustedHumidity

        let tooltipContent =
            sprintf
                "Original: %s; Max: %s"
                (percentageToString adjustedHumidity.OriginalHumidity)
                (percentageToString adjustedHumidity.MaxHumidity)

        let content =
            span [
                Class Tooltip.ClassName
                Tooltip.dataTooltip tooltipContent
            ] [
                str (percentageToString adjustedHumidity.UnboundedAdjustedHumidity)
                Icon.icon [] [ Fa.i [ Fa.Solid.InfoCircle ] [] ]
            ]

        createTableCell color [ content ]


let private expectedSpreadingFactor = 7

let private createTimeStampCell (lastData: PhysicalSensorLastData) =
    lastData.LastTimeStamp
    |> TimeSpan.createElapsedTime
    |> TimeSpan.toHumanReadable
    |> sprintf "vor %s"
    |> fun content ->
        td [] [
            Icon.icon [] [ Fa.i [ Fa.Solid.Clock ] [] ]
            span [] [ str content ]
        ]

let private createIntervalCell (maybeSensorModel: SensorModel option) (lastData: PhysicalSensorLastData) =
    match lastData.LastTimespan, maybeSensorModel with
    | Some interval, Some sensorModel -> createLastIntervalCell sensorModel interval
    | Some interval, None ->
        let roundedMinutes = TimeSpan.toRoundedMinutes interval

        let content = [
            Icon.icon [] [ Fa.i [ Fa.Solid.History ] [] ]
            span [] [
                str (TimeSpan.countToString roundedMinutes "Minute" "Minuten")
            ]
        ]

        createTableCell None content
    | None, _ -> td [] []

let private createLastSpreadingFactorCell (lastData: PhysicalSensorLastData) =
    let lastSpreadingFactorColor =
        if lastData.LastSpreadingFactor <> expectedSpreadingFactor then
            Some dangerColor
        else
            None

    let content = sprintf "SF %d" lastData.LastSpreadingFactor

    createStringTableCell lastSpreadingFactorColor content

let lastDataCells (maybeSensorModel: SensorModel option) (lastData: PhysicalSensorLastData) : ReactElement list =

    [
        createTimeStampCell lastData
        createIntervalCell maybeSensorModel lastData
        createLastSpreadingFactorCell lastData

        match lastData.LastHumidity with
        | Some adjustedHumidity -> createLastHumidityCell adjustedHumidity
        | None -> td [] []
    ]

let sensorToRow
    dispatch
    (users: IDictionary<int, UserDto>)
    (locations: DeviceLocation list)
    (index: int)
    (sensor: PhysicalSensorWithLastData)
    =
    let maybeUsername =
        getUserId sensor.Sensor
        |> Option.bind (Dict.tryGetValue users)
        |> Option.map getFullName

    let maybeLocation =
        (getBaseData sensor.Sensor).LocationId
        |> Option.bind (fun locationId ->
            List.tryFind (fun (location: DeviceLocation) -> location.Id = locationId) locations
        )
        |> Option.map (fun location -> location.Name)

    let lastDataCells =
        match sensor.LastData with
        | Some lastData -> lastDataCells (getSensorModel sensor.Sensor) lastData
        | None -> List.replicate 4 (td [] [])

    tr [ Class "v_centered" ] [
        td [] [ Table.rowIndexString index ]
        td [] [
            div [
                Class "is-flex is-flex-direction-column"
                Style [ GridGap "0.5rem" ]
            ] [
                span [] [ str (getSensorName sensor.Sensor) ]
                span [ Style [ FontSize 13 ] ] [ str (getSensorDeviceEui sensor.Sensor) ]
            ]
        ]
        td [] [
            (getBaseData sensor.Sensor).Note |> Option.defaultValue "" |> str
        ]
        td [] [
            sensorModelAsString sensor.Sensor |> Option.defaultValue "-" |> str
        ]
        td [] [
            match maybeUsername with
            | Some userName ->
                Icon.icon [] [ Fa.i [ Fa.Solid.User ] [] ]
                span [] [ str userName ]
            | None -> ()
        ]
        td [] [
            (getBaseData sensor.Sensor).Condition |> DeviceCondition.label |> str
        ]
        td [] [
            match maybeLocation with
            | Some location ->
                Icon.icon [] [ Fa.i [ Fa.Solid.MapMarkerAlt ] [] ]
                span [] [ str location ]
            | None -> ()
        ]
        yield! lastDataCells
        td [] [
            span [
                Class "is-flex is-justify-content-flex-end"
                Style [ ColumnGap "0.5rem" ]
            ] [
                Button.button [
                    Clickable.onClickPreventDefault (fun _ ->
                        dispatch (PhysicalSensorListMsg.EditSensorModal sensor.Sensor |> PhysicalSensorList)
                    )
                    |> Button.OnClick
                ] [ str "Bearbeiten" ]
                Button.button [
                    Clickable.onClickPreventDefault (fun _ ->
                        dispatch (
                            PhysicalSensorListMsg.DeleteSensor(getSensorName sensor.Sensor)
                            |> PhysicalSensorList
                        )
                    )
                    |> Button.OnClick
                ] [ str "Löschen" ]
            ]
        ]
    ]

let sensorListToTable
    dispatch
    (users: IDictionary<int, UserDto>)
    (locations: DeviceLocation list)
    (sensors: PhysicalSensorWithLastData list)
    =
    Table.table [
        Table.IsFullWidth
        Table.IsStriped
        Table.IsHoverable
    ] [
        thead [] [
            tr [] [
                th [] [ str "#" ]
                th [] [ str "Name (Device EUI)" ]
                th [] [ str "Interne Notiz" ]
                th [] [ str "Sensortyp" ]
                th [] [ str "Besitzer" ]
                th [] [ str "Zustand" ]
                th [] [ str "Standort" ]
                th [] [ str "Letzte Daten" ]
                th [] [ str "Letztes Interval" ]
                th [] [ str "Letzte Datenrate" ]
                th [] [ str "Letzte Luftfeuchtigkeit" ]
                th [] []
            ]
        ]
        tbody [] (List.mapi (sensorToRow dispatch users locations) sensors)
    ]

let createNewDevice dispatch =
    Button.button [
        Button.Color IsLink
        Button.OnClick(fun _ -> dispatch (PhysicalSensorList PhysicalSensorListMsg.OpenNewSensorModal))
    ] [ str "Neuer Sensor anlegen" ]

let refreshTtnButton model dispatch =
    PhysicalSensorRefreshButton.view
        "Sensor aus TTN herunterladen"
        (fun _ -> dispatch (PhysicalSensorList PhysicalSensorListMsg.RefreshTTNSensors))
        model

let refreshTtiButton model dispatch =
    PhysicalSensorRefreshButton.view
        "Sensor aus TTI herunterladen"
        (fun _ -> dispatch (PhysicalSensorList PhysicalSensorListMsg.RefreshTTISensors))
        model

let private toggleFilterDisplayButton dispatch =
    let customOptions = [
        CommonButtons.onClick (fun _ -> dispatch (PhysicalSensorList PhysicalSensorListMsg.ToggleFilterDisplay))
    ]

    CommonButtons.createFilterButton customOptions

let tableButtons ttnButtonModel ttiButtonModel dispatch =
    Level.level [] [
        Level.left [] []
        Level.right [] [
            Level.item [] [
                Field.div [] [ Control.div [] [ createNewDevice dispatch ] ]
            ]
            Level.item [] [
                Field.div [] [
                    Control.div [] [ refreshTtnButton ttnButtonModel dispatch ]
                ]
            ]
            Level.item [] [
                Field.div [] [
                    Control.div [] [ refreshTtiButton ttiButtonModel dispatch ]
                ]
            ]
            Level.item [] [
                Field.div [] [
                    Control.div [] [ toggleFilterDisplayButton dispatch ]
                ]
            ]
        ]
    ]

let private filtersView dispatch (data: DataModel) =
    Box.box' [ GenericOption.CustomClass "filter_box" ] [
        Columns.columns [ Columns.IsMultiline ] [
            Column.column [ Column.Width(Screen.All, Column.Is3) ] [
                Field.div [] [
                    Label.label [] [ str "Name" ]
                    Control.div [ Control.HasIconLeft ] [
                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.Search ] [] ]

                        Input.text [
                            data.NameFilter |> Option.defaultValue "" |> Input.ValueOrDefault
                            Input.OnChange(fun event ->
                                event.Value
                                |> String.toOption
                                |> PhysicalSensorListMsg.NameFilterChanged
                                |> dispatch
                            )
                        ]
                    ]
                ]
            ]
            Column.column [ Column.Width(Screen.All, Column.Is3) ] [
                Field.div [] [
                    Label.label [] [ str "Device EUI" ]
                    Control.div [ Control.HasIconLeft ] [
                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.Search ] [] ]

                        Input.text [
                            data.DeviceEuiFilter |> Option.defaultValue "" |> Input.ValueOrDefault
                            Input.OnChange(fun event ->
                                event.Value
                                |> String.toOption
                                |> PhysicalSensorListMsg.DeviceEuiFilterChanged
                                |> dispatch
                            )
                        ]
                    ]
                ]
            ]
            Column.column [ Column.Width(Screen.All, Column.Is3) ] [
                Field.div [] [
                    Label.label [] [ str "Interne Notiz" ]

                    Control.div [ Control.HasIconLeft ] [
                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.Search ] [] ]

                        Input.text [
                            data.NoteFilter |> Option.defaultValue "" |> Input.ValueOrDefault
                            Input.OnChange(fun event ->
                                event.Value
                                |> String.toOption
                                |> PhysicalSensorListMsg.NoteFilterChanged
                                |> dispatch
                            )
                        ]
                    ]
                ]
            ]
            Column.column [ Column.Width(Screen.All, Column.Is3) ] [
                Field.div [] [
                    Label.label [] [ str "Nach Benutzer filtern" ]
                    Control.div [ Control.HasIconLeft ] [
                        UserFilter.view
                            (PhysicalSensorListMsg.UserFilterChanged >> dispatch)
                            data.UserFilter
                            (List.ofDictValues data.Users)

                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.User ] [] ]
                    ]
                ]
            ]
            Column.column [ Column.Width(Screen.All, Column.Is3) ] [
                Field.div [] [
                    Label.label [] [ str "Nach Standort filtern" ]
                    Control.div [ Control.HasIconLeft ] [
                        DeviceLocationFilter.view
                            data.DeviceLocations
                            (PhysicalSensorListMsg.LocationFilterChanged >> dispatch)
                            data.LocationFilter

                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.MapMarkerAlt ] [] ]
                    ]
                ]
            ]
            Column.column [ Column.Width(Screen.All, Column.Is3) ] [
                Field.div [] [
                    Label.label [] [ str "Nach Zustand filtern" ]
                    Control.div [ Control.HasIconLeft ] [
                        DeviceConditionFilter.view
                            (PhysicalSensorListMsg.ConditionFilterChanged >> dispatch)
                            data.ConditionFilter

                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.Search ] [] ]
                    ]
                ]
            ]
        ]
    ]


let private dataView dispatch (data: DataModel) =
    let tableButtons = tableButtons data.TTNRefreshButton data.TTIRefreshButton dispatch

    let table =
        sensorListToTable dispatch data.Users data.DeviceLocations data.DisplayedSensors

    let modal =
        data.Modal
        |> Option.map (Forms.PhysicalSensor.view (PhysicalSensorList >> dispatch))
        |> Option.defaultValue (div [] [])

    Container.container [ Container.IsFluid ] [
        Heading.h1 [] [ str "Physische Sensoren Liste" ]

        tableButtons

        match data.DisplayFilters with
        | true -> filtersView (PhysicalSensorList >> dispatch) data
        | false -> ()

        Table.scrollableTable table
        modal
    ]


let view (model: Model) dispatch = Loadable.view (dataView dispatch) model